<script setup lang="ts">
import type { BonusProductsApi } from '@/components/games/rewardsPopup/RewardsPopup.ts'
import BannerBg from '@/assets/components/goatConversionCoinsDialog/popup_bg.webp?url'
import { rewardsPopup } from '@/components/games/rewardsPopup/RewardsPopup.ts'
import { gamesUtils } from '@/compositions/gamesUtils.ts'

interface BonusItem {
  icon: string
  bonusAmount: number
  bonusAmountHuman: string
  title: string
  name: string
}

const { routeGame, triggerApi } = gamesUtils
const { t } = useI18n()
const { windowHeight, isMobileDevice, windowWidth } = tools
const route = useRoute()

onMounted(() => {
})
onUnmounted(() => {
  rewardsPopup.value.visible = false
})
const bonuses = computed<BonusItem[]>(() => {
  return (rewardsPopup.value?.rewardsApi?.[routeGame.value?.gameKey || '']?.items?.map((item) => {
    if ((item.title || '').toLowerCase() === 'coin') {
      return {
        icon: routeGame.value?.loyalty?.icon || '',
        bonusAmount: item?.amount || 0,
        bonusAmountHuman: gamesUtils.numbersShortHuman(item?.amount || 0),
        title: item.title || '',
        name: item?.label || t('offerTileItems.loyaltyPoints') || item?.title || ''
      }
    }
    return {
      icon: item.asset ? (item.asset.startsWith('http') ? item.asset : `${routeGame.value?.imgStorageUrl || ''}${item.asset}`) : '',
      bonusAmount: item?.amount || 0,
      bonusAmountHuman: gamesUtils.numbersShortHuman(item?.amount || 0),
      title: item.title || '',
      name: item?.label || item?.title || ''
    }
  }) || []).sort((a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()))
})

const products = computed<BonusProductsApi[]>(() => {
  return rewardsPopup.value?.rewardsApi?.[routeGame.value?.gameKey || '']?.products?.map((item) => {
    return { ...item, asset: item?.asset ? (item.asset.startsWith('http') ? item.asset : `${routeGame.value?.imgStorageUrl || ''}${item.asset}`) : '' }
  }) || []
})
let myConfetti: import('canvas-confetti').CreateTypes | null = null
function onClose() {
  rewardsPopup.value.visible = false
  rewardsPopup.value.rewardsApi = {}
  triggerApi.callTrigger(100)
  if (myConfetti) {
    myConfetti.reset()
    myConfetti = null
  }
}
const deepLinkUrl = computed(() => {
  if (routeGame.value?.idApp) {
    return gamesUtils.buildDeepLink(
      routeGame.value?.idApp || '',
      String(route?.path),
      routeGame.value?.gameKey || '',
      '',
      false
    )
  }
  return ''
})
function onClickDeepLink(event: MouseEvent) {
  const target = event.currentTarget as HTMLAnchorElement
  const url = target.href
  if (url) {
    rewardsPopup.value.visible = false
    window.location.href = url
  }
}
const devMode = computed(() => {
  return import.meta.env.VITE_PROD !== 'on'
})
const heightBreakPoint = 550
watch(() => bonuses.value.length + products.value.length, () => {
  if (bonuses.value.length + products.value.length === 0) {
    rewardsPopup.value.visible = false
  }
})
const enabledDeepLinkButton = false // import.meta.env.VITE_PROD !== 'on'

async function onOpened() {
  if (import.meta.env.SSR)
    return
  try {
    const { default: confetti } = await import('canvas-confetti')
    myConfetti = confetti.create(undefined, { resize: true })
    myConfetti({
      particleCount: 150,
      spread: 70,
      origin: { y: 0.6 }
    })
  } catch {
    // confetti is a cosmetic effect — a failed/broken dynamic import shouldn't crash the popup
  }
}
</script>

<template>
  <el-dialog
    v-model="rewardsPopup.visible"
    top="0px"
    modal-class="rewards-dialog-modal bg-[#1c1c1c]/90"
    class="rewards-dialog bg-transparent"
    width="100%"
    style="--el-dialog-padding-primary: 0"
    destroy-on-close
    :show-close="false"
    :z-index="61"
    fullscreen
    :close-on-click-modal="false"
    header-class="hidden"
    body-class="h-full"
    @closed="onClose"
    @opened="onOpened"
  >
    <div class="h-full w-full big:h-auto  text-white big:pt-[30px]">
      <div class="bg-[#051A1E] w-full min-h-full big:min-h-auto max-w-xl mx-auto relative big:rounded-xl overflow-hidden">
        <template v-if="products.length">
          <div
            class="z-10  h-full min-h-[max(400px,94vh)]"
          >
            <el-scrollbar
              max-height="95vh"
            >
              <div>
                <div class="bg-[#051A1E] relative">
                  <div class="pb-5">
                    <h1
                      class="text-center uppercase font-black leading-tight text-lg sm:text-xl big:text-2xl px-4 py-5 big:py-10 max-w-lg mx-auto"
                    >
                      {{ t('rewardsPopup.purchaseComplete') }}
                    </h1>
                    <loyalty-progress-animation />
                    <div class="max-w-lg mx-auto">
                      <div class="text-left capitalize font-black leading-tight text-base sm:text-lg big:text-xl pl-6 pr-4 mt-5 mb-5">
                        {{ t('rewardsPopup.yourPurchases') }}
                      </div>
                      <div class="flex flex-col gap-y-4 px-4">
                        <div
                          v-for="offer in products"
                          :key="offer.id"
                        >
                          <div class="flex flex-row gap-1 items-center bg-white rounded-xl p-2 pr-4 text-black">
                            <div class="w-3/12 pr-2">
                              <img
                                v-if="offer?.asset"
                                :src="offer.asset"
                                loading="lazy"
                                :alt="t('cartDrawer.offerImgAlt')"
                                class="rounded-md"
                              >
                            </div>
                            <div class="w-9/12 relative">
                              <div class="font-bold truncate">
                                {{ offer?.name }}
                              </div>
                              <div class="flex flex-row flex-wrap gap-2 big:gap-x-3 text-sm text-neutral-600 justify-between">
                                <div>{{ offer?.price?.format || '' }}</div>
                                <div class="whitespace-nowrap">
                                  x{{ offer?.purchasedAmount || 1 }}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div
                        v-if="bonuses.length"
                        class="text-left capitalize font-black leading-tight text-base sm:text-lg big:text-xl pl-6 pr-4 mt-10 mb-5"
                      >
                        {{ t('rewardsPopup.bonusesSummary') }}
                      </div>
                      <div
                        class="text-white grid grid-cols-[auto_1fr] gap-y-3 gap-x-5 items-center max-w-[85%] mx-auto"
                      >
                        <template
                          v-for="(bonus, index) in bonuses"
                          :key="index"
                        >
                          <div class="item-box bg-white w-11 h-11 mini:w-12 mini:h-12 rounded-lg border-2 border-white shadow-xs relative">
                            <div class="absolute inset-0">
                              <template v-if="bonus.icon">
                                <img
                                  :alt="t('offerTileItems.itemAlt')"
                                  :src="bonus.icon"
                                  class="block max-w-9 mini:max-w-11 big:max-w-9 mx-auto"
                                >
                              </template>
                            </div>
                            <div
                              v-if="bonus.bonusAmountHuman"
                              class="text-center font-semibold leading-none p-0.5 whitespace-nowrap z-10 text-[0.8rem] mini:text-[0.95rem] big:text-[0.8rem] lg:text-[0.9rem]"
                            >
                              <div
                                class="gs-text-shadow font-black text-white text-center absolute left-1/2 -translate-x-1/2 bottom-0.5"
                                :class="{ 'tracking-tighter': bonus.bonusAmountHuman.length > 4 }"
                              >
                                {{ bonus.bonusAmountHuman }}
                              </div>
                            </div>
                          </div>
                          <div class="font-bold text-sm">
                            x<span class="capitalize">{{ bonus.bonusAmountHuman }} {{ bonus.name }}</span>
                          </div>
                        </template>
                      </div>
                      <div
                        v-if="windowHeight > heightBreakPoint"
                        class="pb-[max(13vh,250px)]"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </el-scrollbar>
            <div
              class="bg-[#0a343b]/98 py-5"
              :class="{
                'absolute bottom-0 left-0 right-0': windowHeight > heightBreakPoint
              }"
            >
              <div class="max-w-lg mx-auto flex flex-col items-center justify-around gap-y-4 min-h-[130px]">
                <div class="text-center text-lg font-bold flex items-center justify-center gap-6">
                  <el-image
                    v-if="routeGame?.icon"
                    :src="routeGame.icon"
                    :alt="`logo ${routeGame.name}`"
                    class="w-12 rounded-lg"
                  />
                  <template v-if="enabledDeepLinkButton && deepLinkUrl.length && (isMobileDevice || (devMode && windowWidth < 640))">
                    <a
                      :href="deepLinkUrl"
                      @click.prevent="onClickDeepLink"
                    >
                      <el-button
                        color="#2c7c8b"
                        class="text-white rounded-lg uppercase text-xl mt-3 mb-2 big:mb-5 font-black px-3"
                        size="large"
                        style="--el-button-size: 47px;"
                      >
                        {{ t('rewardsPopup.openGame') }}
                      </el-button>
                    </a>
                  </template>
                  <template v-else>
                    {{ t('rewardsPopup.congratulations') }}
                  </template>
                </div>
                <el-button
                  color="transparent"
                  class="text-neutral-200 uppercase font-black rounded-lg tracking-wide text-base big:px-5 border-white"
                  size="default"
                  @click="rewardsPopup.visible = false"
                >
                  {{ t('rewardsPopup.continueShopping') }}
                </el-button>
              </div>
            </div>
          </div>
        </template>
        <template v-else>
          <button
            class="text-2xl transition hover:rotate-90 absolute right-1.5 top-1 z-20"
            @click="rewardsPopup.visible = false"
          >
            <icon-ify
              icon="material-symbols:close"
              class=""
            />
          </button>
          <div
            class="bg-cover bg-top aspect-[1.27] absolute top-0 left-0 right-0 z-0 opacity-40"
            :style="{ backgroundImage: `url('${BannerBg}')` }"
          />
          <div class="aspect-[2.7]" />
          <div class="z-10 relative">
            <h1 class="text-center uppercase font-black leading-tight text-lg sm:text-xl big:text-2xl px-4 mt-5">
              {{ t('rewardsPopup.claimRewards') }}
            </h1>
            <div
              class="text-white grid grid-cols-[auto_1fr] gap-y-3 gap-x-5 items-center max-w-[85%] mx-auto mt-20"
            >
              <template
                v-for="(bonus, index) in bonuses"
                :key="index"
              >
                <div class="item-box bg-white w-11 h-11 mini:w-12 mini:h-12 rounded-lg border-2 border-white shadow-xs relative">
                  <div class="absolute inset-0">
                    <template v-if="bonus.icon">
                      <img
                        :alt="t('offerTileItems.itemAlt')"
                        :src="bonus.icon"
                        class="block max-w-9 mini:max-w-11 big:max-w-9 mx-auto"
                      >
                    </template>
                  </div>
                  <div
                    v-if="bonus.bonusAmountHuman"
                    class="text-center font-semibold leading-none p-0.5 whitespace-nowrap z-10 text-[0.8rem] mini:text-[0.95rem] big:text-[0.8rem] lg:text-[0.9rem]"
                  >
                    <div
                      class="gs-text-shadow font-black text-white text-center absolute left-1/2 -translate-x-1/2 bottom-0.5"
                      :class="{ 'tracking-tighter': bonus.bonusAmountHuman.length > 4 }"
                    >
                      {{ bonus.bonusAmountHuman }}
                    </div>
                  </div>
                </div>
                <div class="font-bold text-sm">
                  +<span class="capitalize">{{ bonus.bonusAmountHuman }} {{ bonus.name }}</span>
                </div>
              </template>
            </div>
          </div>
          <div class="text-center mt-16 mb-32 z-10 relative">
            <el-button
              color="#38a6bb"
              class="text-white uppercase font-black rounded-lg tracking-wide text-xl big:px-5 h-10 min-w-36"
              size="large"
              @click="rewardsPopup.visible = false"
            >
              {{ t('rewardsPopup.claim') }}
            </el-button>
          </div>
        </template>
      </div>
    </div>
  </el-dialog>
</template>

<style lang="postcss">

</style>
